import api from './api';

/**
 * Training Service
 * Handles training modules and progress tracking
 */

/**
 * Get training records for current user
 * @param {object} params - Query parameters
 * @param {string} params.term - Filter by academic term
 * @param {string} params.programType - Filter by program type (si, ta, la)
 */
export const getMyTraining = async (params = {}) => {
    const response = await api.get('/training/mine', { params });
    return response.data;
};

/**
 * Get all available training modules
 * @param {object} params - Query parameters
 * @param {string} params.programType - Filter by program type
 * @param {string} params.type - Filter by module type (required, elective, program-specific)
 */
export const getModules = async (params = {}) => {
    const response = await api.get('/training/modules', { params });
    return response.data;
};

/**
 * Get a specific training module with user progress
 * @param {string} moduleId - Module ID
 */
export const getModuleById = async (moduleId) => {
    const response = await api.get(`/training/modules/${moduleId}`);
    return response.data;
};

/**
 * Update progress on a training module
 * @param {object} data - Progress data
 * @param {string} data.moduleId - Module ID
 * @param {string} data.programType - Program type context
 * @param {string} data.term - Academic term
 * @param {number} data.progress - Progress percentage (0-100)
 * @param {string} data.status - Status (not_started, in_progress, completed)
 */
export const updateProgress = async (data) => {
    const response = await api.post('/training/progress', data);
    return response.data;
};

/**
 * Mark a training module as complete
 * @param {object} data - Completion data
 * @param {string} data.moduleId - Module ID
 * @param {string} data.programType - Program type context
 * @param {string} data.term - Academic term
 * @param {number} data.score - Optional quiz score
 */
export const completeModule = async (data) => {
    const response = await api.post('/training/complete', data);
    return response.data;
};

/**
 * Get certificates for current user
 */
export const getMyCertificates = async () => {
    const response = await api.get('/training/certificates');
    return response.data;
};

/**
 * Get training progress by program (coordinator view)
 * @param {string} programType - Program type (si, ta, la)
 * @param {object} params - Query parameters
 * @param {string} params.term - Filter by term
 * @param {number} params.limit - Results limit
 * @param {number} params.offset - Results offset
 */
export const getTrainingByProgram = async (programType, params = {}) => {
    const response = await api.get(`/training/program/${programType}`, {
        params,
    });
    return response.data;
};

/**
 * Issue certificate for a completed module (coordinator only)
 * @param {object} data - Certificate data
 * @param {string} data.userId - User ID
 * @param {string} data.moduleId - Module ID
 * @param {string} data.programType - Program type
 * @param {string} data.term - Academic term
 */
export const issueCertificate = async (data) => {
    const response = await api.post('/training/certificate', data);
    return response.data;
};

/**
 * Create a new training module (admin only)
 * @param {object} moduleData - Module data
 */
export const createModule = async (moduleData) => {
    const response = await api.post('/training/modules', moduleData);
    return response.data;
};

/**
 * Update a training module (admin only)
 * @param {string} moduleId - Module ID
 * @param {object} updates - Module updates
 */
export const updateModule = async (moduleId, updates) => {
    const response = await api.put(`/training/modules/${moduleId}`, updates);
    return response.data;
};

export default {
    getMyTraining,
    getModules,
    getModuleById,
    updateProgress,
    completeModule,
    getMyCertificates,
    getTrainingByProgram,
    issueCertificate,
    createModule,
    updateModule,
};
